/**
 * Hero proof card — the redacted appeal excerpt that anchors the
 * landing hero. Pulled verbatim from the design reference so the
 * sample case (lumbar imaging) stays consistent across marketing
 * surfaces.
 */
export function ProofCard() {
  return (
    <aside
      aria-label="Sample appeal excerpt"
      className="overflow-hidden rounded-md border border-line bg-surface"
    >
      <header className="flex items-center justify-between border-b border-line px-[18px] py-3.5">
        <span className="text-[10px] uppercase tracking-[0.14em] text-ink-hint">
          Draft · Section 03 of 06
        </span>
        <span className="pill-verified">Verified</span>
      </header>

      <div className="px-7 pb-5 pt-6">
        <div className="mb-3 text-[10px] uppercase tracking-[0.14em] text-ink-hint">
          Medical necessity
        </div>
        <p className="mb-4 font-serif text-[14.5px] leading-[1.75] text-ink">
          Mr. <span className="redact">[Patient]</span> presents with subacute lumbar pain
          refractory to a documented six-week trial of conservative management. The American
          College of Radiology Appropriateness Criteria explicitly designate advanced imaging as{" "}
          <em className="italic text-ink">&ldquo;Usually Appropriate&rdquo;</em>
          <sup className="cite">[1]</sup> for patients whose symptoms persist beyond six weeks of
          conservative therapy.
        </p>
        <p className="font-serif text-[14.5px] leading-[1.75] text-ink">
          The criterion the insurer cites —{" "}
          <em className="italic text-ink">
            &ldquo;duration inadequate to justify advanced imaging&rdquo;
          </em>
          <sup className="cite">[2]</sup> — does not match the documented timeline.
        </p>
      </div>

      <footer className="border-t border-line bg-parchment-soft px-7 py-3.5">
        <SourceRow num="[1]" text="ACR Appropriateness Criteria, Low Back Pain — American College of Radiology, 2023" />
        <SourceRow num="[2]" text="Anthem PPO Policy CG-MED-58, §4.2 — Carrier-supplied determination letter" />
      </footer>
    </aside>
  );
}

function SourceRow({ num, text }: { num: string; text: string }) {
  return (
    <div className="mb-2 flex items-start gap-3 last:mb-0">
      <span className="min-w-[18px] font-mono text-[10px] font-medium text-brick">{num}</span>
      <span className="text-[11px] leading-[1.5] text-ink-soft">{text}</span>
    </div>
  );
}
